import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { UpdateFormData } from "../../hooks/useUpdates";

interface CreateUpdateProps {
  open: boolean;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (data: UpdateFormData) => Promise<void>;
}

const defaultForm: UpdateFormData = {
  title: "",
  description: "",
  type: "daily",
};

export function CreateUpdate({
  open,
  saving,
  error,
  onClose,
  onSave,
}: CreateUpdateProps) {
  const [form, setForm] = useState<UpdateFormData>(defaultForm);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleClose = () => {
    setForm(defaultForm);
    setValidationError(null);
    onClose();
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setValidationError("Title is required.");
      return;
    }
    setValidationError(null);
    await onSave(form);
    setForm(defaultForm);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Post an update</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {(error || validationError) && (
            <Alert severity="error">{validationError || error}</Alert>
          )}
          <TextField
            select
            label="Type"
            value={form.type}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                type: e.target.value as UpdateFormData["type"],
              }))
            }
            size="small"
          >
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="milestone">Milestone</MenuItem>
          </TextField>
          <TextField
            label="Title"
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            required
            size="small"
            placeholder="e.g. Framing complete on the north wall"
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, description: e.target.value }))
            }
            multiline
            rows={4}
            size="small"
            placeholder="Optional details about today's progress..."
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Posting..." : "Post update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
